import type { ReactNode } from "react";
import { genreTags, lengthOptions, regionOptions, yearRanges } from "@/mocks/categories";

export type FilterState = {
  region: string;
  year: string;
  length: string;
  tags: string[];
};

type FilterPanelProps = {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  activeCount: number;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="py-4 border-b border-background-300/50 last:border-b-0">
    <h3 className="font-label text-[11px] tracking-[0.2em] uppercase text-foreground-500">
      {title}
    </h3>
    <div className="mt-3">{children}</div>
  </div>
);

export default function FilterPanel({ value, onChange, onReset, activeCount }: FilterPanelProps) {
  const toggleTag = (tag: string) => {
    const tags = value.tags.includes(tag)
      ? value.tags.filter((item) => item !== tag)
      : [...value.tags, tag];
    onChange({ ...value, tags });
  };

  return (
    <div className="w-full rounded-lg bg-background-100 border border-background-300/60">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-background-300/50">
        <h2 className="text-sm text-foreground-900 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-700" />
          筛选条件
          {activeCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary-500 text-[10px] text-foreground-950">
              {activeCount}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-[11px] text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          重置
        </button>
      </div>

      <div className="px-4 pb-2">
        <Section title="地区">
          <div className="flex flex-wrap gap-1.5">
            {regionOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, region: option.value })}
                className={`px-2.5 py-1.5 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
                  value.region === option.value
                    ? "bg-primary-500 text-foreground-950"
                    : "bg-background-200/70 text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="年份">
          <div className="flex flex-col gap-1.5">
            {yearRanges.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, year: option.value })}
                className={`text-left px-2.5 py-2 rounded-md text-xs cursor-pointer transition-colors ${
                  value.year === option.value
                    ? "bg-primary-500/20 text-accent-500 border border-primary-500/40"
                    : "text-foreground-600 hover:bg-background-200/70 border border-transparent"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="片长">
          <div className="flex flex-col gap-1.5">
            {lengthOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ ...value, length: option.value })}
                className={`text-left px-2.5 py-2 rounded-md text-xs cursor-pointer transition-colors ${
                  value.length === option.value
                    ? "bg-primary-500/20 text-accent-500 border border-primary-500/40"
                    : "text-foreground-600 hover:bg-background-200/70 border border-transparent"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="主题标签">
          <div className="flex flex-wrap gap-1.5">
            {genreTags.map((tag) => {
              const active = value.tags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1.5 rounded-md text-xs cursor-pointer transition-colors whitespace-nowrap ${
                    active
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-200/70 text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}